// The context slot (ui-map-interface.md §12.0 "The context slot", mockup
// round 083 cards 10 and 13).
//
// Every card that explains a thing opens in ONE place: on a desktop at the
// bottom-left of the map over the console, on a telephone as the context
// sheet over the tab bar. `ui/layout.ts` publishes the place (`--ctx-l`,
// `--ctx-b`, `--ctx-w`), and the stylesheet stands every host of the
// class `ctxcard` there. One card stands at a time: a host claims the
// slot, and the slot shows the claim of the highest rank and holds the
// others back with the class `ctx-wait`, so no two cards stand on top of
// each other and text never shows through text (§2.3).
//
// The rank: an ask that waits for an answer (the rename card, then the
// commit card, then a picker: the captain picker or the start sheet of a
// dive, R1893) outranks the build card of a catalogue item, which
// outranks the card of the selection, which outranks the step card of an
// arcade drill (R1938). When the ask is answered, the card under it comes
// back.
//
// The overlay holds one `ContextSlot`. A lane that puts a new card into the
// slot calls `host` once with its element and `claim` on every render.

/** The cards the slot holds, by name. */
export type SlotCard = 'rename' | 'commit' | 'pick' | 'info' | 'selection' | 'step';

/** The rank of the cards, the first wins the slot. The step card of an
 *  arcade drill stands last: a selection takes the sheet over it, and the
 *  step folds to its strip (arcade.md §17.8, R1938). The season map has no
 *  step card, so the rank changes nothing there. */
export const SLOT_RANK: readonly SlotCard[] = ['rename', 'commit', 'pick', 'info', 'selection', 'step'];

/**
 * The size of a card in the raised context sheet (round 083 open point 3):
 * a map object takes at most `ctx_object_vh` of the height, a catalogue
 * card `ctx_catalogue_vh`, because the map matters less while the player
 * builds.
 */
export type SlotKind = 'object' | 'catalogue';

/** The kind of each card: the build card and a picker, whose list of
 *  captains is long, are the catalogue cards. */
export function slotKind(card: SlotCard): SlotKind {
  return card === 'info' || card === 'pick' ? 'catalogue' : 'object';
}

/** The card that holds the slot: the claim of the highest rank, or null. */
export function slotOwner(claims: Partial<Record<SlotCard, boolean>>): SlotCard | null {
  for (const c of SLOT_RANK) if (claims[c]) return c;
  return null;
}

/** One slot and its hosts. It holds no card markup: each host writes its own. */
export class ContextSlot {
  private hosts = new Map<SlotCard, HTMLElement>();
  private claims: Partial<Record<SlotCard, boolean>> = {};
  private ownerNow: SlotCard | null = null;

  /** `root` is `#ui`: it carries `data-ctx` with the name of the owner. */
  constructor(private readonly root: HTMLElement) {}

  /** Registers the element of one card. The element takes the class
   *  `ctxcard`, which stands it in the slot, and `cat` for a catalogue
   *  card, which gives it the larger cap of the raised sheet. */
  host(card: SlotCard, el: HTMLElement): void {
    this.hosts.set(card, el);
    el.classList.add('ctxcard');
    el.classList.toggle('cat', slotKind(card) === 'catalogue');
    this.sync();
  }

  /** Takes the element of one card out of the slot: its claim goes, and
   *  the element loses the marks of the slot. An arcade step card leaves
   *  the slot when a telephone turns on its side (R1940). */
  unhost(card: SlotCard): void {
    const el = this.hosts.get(card);
    if (!el) return;
    this.hosts.delete(card);
    delete this.claims[card];
    el.classList.remove('ctxcard', 'cat', 'ctx-wait');
    this.sync();
  }

  /** Says whether a card wants the slot now, and shows the owner. */
  claim(card: SlotCard, on: boolean): SlotCard | null {
    this.claims[card] = on;
    return this.sync();
  }

  /** The card that holds the slot now. */
  owner(): SlotCard | null { return this.ownerNow; }

  /** Writes the owner: `ctx-wait` on every other host, `data-ctx` on `#ui`. */
  sync(): SlotCard | null {
    const owner = slotOwner(this.claims);
    this.ownerNow = owner;
    for (const [card, el] of this.hosts) el.classList.toggle('ctx-wait', owner !== null && card !== owner);
    if (owner) this.root.dataset.ctx = owner; else delete this.root.dataset.ctx;
    return owner;
  }
}
